import { useState, useCallback, useRef } from 'react';
import { uploadAttachment, validateFile } from '../../../../../services/attachmentService';

/**
 * useFileUpload Hook
 * 
 * Handles drag-and-drop and file input upload logic with:
 * - Drag state management
 * - File selection handling
 * - Real upload to R2 via attachmentService
 * - Upload progress tracking
 * - Version notes support
 * - Error handling
 */
const useFileUpload = ({ missionId, onUploadComplete, onError, onRequestNote }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Upload a single file with optional note
  const uploadFile = useCallback(async (file, note = null) => {
    if (!missionId) {
      onError?.('No mission ID provided');
      return null;
    }

    // Validate first
    const validation = validateFile(file);
    if (!validation.valid) {
      onError?.(validation.error);
      return null;
    }

    setIsUploading(true);
    setUploadProgress({ filename: file.name, progress: 0 });

    try {
      const result = await uploadAttachment(missionId, file, (progress) => {
        setUploadProgress({ filename: file.name, progress: Math.round(progress) });
      }, note);

      // Brief delay to show 100% before clearing
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Notify parent of successful upload
      onUploadComplete?.(result);
      return result;
    } catch (error) {
      console.error('Upload failed:', error);
      onError?.(error.message || 'Upload failed');
      return null;
    } finally {
      setUploadProgress(null);
      setIsUploading(false);
    }
  }, [missionId, onUploadComplete, onError]);

  // Process files - either upload directly or request note first
  const processFiles = useCallback(async (files, note = null) => {
    // Reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // If onRequestNote is provided and no note given, ask for note first
    if (onRequestNote && !note && files.length === 1) {
      setPendingFiles(files);
      onRequestNote(files[0]);
      return;
    }

    // Upload files sequentially
    for (const file of files) {
      await uploadFile(file, note);
    }
    
    setPendingFiles(null);
  }, [uploadFile, onRequestNote]);

  // Called when user provides a note (or skips)
  const confirmUploadWithNote = useCallback(async (note = null) => {
    if (!pendingFiles) return;
    
    const files = pendingFiles;
    setPendingFiles(null);
    
    for (const file of files) {
      await uploadFile(file, note);
    }
  }, [pendingFiles, uploadFile]);

  // Cancel pending upload
  const cancelPendingUpload = useCallback(() => {
    setPendingFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) {
      setIsDragging(true);
    }
  }, [isUploading]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === dropZoneRef.current) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (isUploading) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFiles(files);
    }
  }, [isUploading, processFiles]);

  const handleFileSelect = useCallback((e) => {
    if (isUploading) return;
    
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processFiles(files);
    }
  }, [isUploading, processFiles]);

  const openFilePicker = useCallback(() => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  }, [isUploading]);

  return {
    isDragging,
    uploadProgress,
    isUploading,
    pendingFiles,
    fileInputRef,
    dropZoneRef,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileSelect,
    openFilePicker,
    confirmUploadWithNote,
    cancelPendingUpload,
  };
};

export default useFileUpload;
